import React, { useCallback, useEffect, useRef, useState } from "react"
import { FormHandles } from "@unform/core"
import * as Yup from "yup"
import api from "../../services/api"
import { useToast } from "../../hooks/toast"

import IUser from "../../models/IUsers"

import {
  ButtonsContainer,
  Container,
  Content,
  FieldsContainer,
  GroupBox,
  Main,
  SearchCard,
  SearchContainer,
  SmallButton,
  StyledForm,
} from "./styles"

import Header from "../../components/Header"
import Sidebar from "../../components/Sidebar"

import Input from "../../components/InputUnform"

import { FiCreditCard, FiLock, FiMail, FiUser } from "react-icons/fi"
import Pagination from "../../components/Pagination"
import RegistersButton from "../../components/RegistersButton"
import getValidationErrors from "../../utils/getValidationErrors"

interface RegisterUserFormData {
  name: string
  email: string
  password: string
}

const Users: React.FC = () => {
  const { addToast } = useToast()

  const formRef = useRef<FormHandles>(null)
  const serachContainerRef = useRef<HTMLDivElement>(null)

  const [users, setUsers] = useState<IUser[]>([])

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  const [selectedUser, setSelectedUser] = useState<IUser | undefined>(undefined)

  //Pesquisa
  const [nameSearch, setNameSearch] = useState<string | undefined>(undefined)
  const [emailSearch, setEmailSearch] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [limit, setLimit] = useState(0)

  const handleSearch = useCallback(async () => {
    try {
      const response = await api.get("/users", {
        params: {
          page,
          name: nameSearch,
          email: emailSearch,
          limit,
        },
      })

      setUsers(response.data.users)
      setTotalPages(response.data.totalPages)
    } catch (err) {
      addToast({
        type: "error",
        title: "Erro ao Pesquisar",
        description: "Ocorreu um erro ao pesquisar, tente novamente mais tarde",
      })
    }
  }, [addToast, page, limit, nameSearch, emailSearch])

  const handleClear = useCallback(() => {
    formRef.current?.clearField("name")
    formRef.current?.clearField("email")
    formRef.current?.clearField("password")

    setName("")
    setEmail("")
    setPassword("")

    setSelectedUser(undefined)
  }, [])

  const handleResetExpiration = useCallback(async () => {
    if (!selectedUser) {
      addToast({
        type: "error",
        title: "Erro ao Redefinir",
        description: "Nenhum usuário selecionado para redefinir, verifique",
      })

      return
    }

    await api.patch(`/reset-expiration/${selectedUser.id}`)

    handleClear()

    addToast({
      type: "success",
      title: "Confirmado",
      description:
        "Data de expiração redefinida. O usuário já pode acessar a aplicação",
    })
  }, [addToast, selectedUser, handleClear])

  const handlePageClick = useCallback((page: number) => {
    setPage(page)
  }, [])

  const handleSubmit = useCallback(
    async (data: RegisterUserFormData) => {
      try {
        formRef.current?.setErrors({})

        const schema = Yup.object().shape({
          name: Yup.string().required("Nome do usuário é obrigatório"),
          email: Yup.string().required("Email do usuário é obrigatória"),
          password: Yup.string().required("Senha do usuário é obrigatória"),
        })

        await schema.validate(data, {
          abortEarly: false,
        })

        await api.post("/users", {
          name: data.name,
          email: data.email,
          password: data.password,
        })
      } catch (err) {
        addToast({
          type: "error",
          title: "Erro no Cadastro",
          description:
            "Ocorreu um erro ao fazer cadastro, tente novamente mais tarde",
        })

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err)

          formRef.current?.setErrors(errors)
        }

        return
      }

      handleSearch()
      handleClear()

      addToast({
        type: "success",
        title: "Parabéns!",
        description: "Cadastro realizado com sucesso",
      })
    },
    [addToast, handleSearch, handleClear]
  )

  useEffect(() => {
    if (limit > 0) {
      handleSearch()
    }
  }, [page, limit, nameSearch, handleSearch])

  useEffect(() => {
    const updateLimit = () => {
      const altura = serachContainerRef.current?.clientHeight
      if (altura) {
        setLimit(Math.floor(altura / 60))
      }
    }

    updateLimit()

    window.addEventListener("resize", updateLimit)

    return () => window.removeEventListener("resize", updateLimit)
  }, [])

  return (
    <Container>
      <Header />

      <Main>
        <Sidebar />

        <Content>
          <h1>Usuários</h1>

          <GroupBox>
            <StyledForm ref={formRef} onSubmit={handleSubmit}>
              <FieldsContainer>
                <Input
                  name="name"
                  icon={FiUser}
                  type="text"
                  placeholder="Nome do Usuário"
                  containerStyle={{ flex: 1 }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!!selectedUser}
                />

                <Input
                  name="email"
                  icon={FiMail}
                  type="text"
                  placeholder="Email do Usuário"
                  containerStyle={{ flex: 1 }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!selectedUser}
                />

                <Input
                  name="password"
                  icon={FiLock}
                  type="password"
                  placeholder="Senha do Usuário"
                  containerStyle={{ flex: 1 }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!!selectedUser}
                />

                <SmallButton
                  className="search"
                  type="button"
                  title="Redefenir expiração do acesso"
                  onClick={handleResetExpiration}
                >
                  <FiCreditCard />
                </SmallButton>
              </FieldsContainer>

              <h3>Pesquisa</h3>

              <FieldsContainer>
                <Input
                  name="nameSearch"
                  icon={FiUser}
                  type="text"
                  placeholder="Nome do Usuário"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  containerStyle={{ flex: 1 }}
                />

                <Input
                  name="emailSearch"
                  icon={FiMail}
                  type="text"
                  placeholder="Email do Usuário"
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  containerStyle={{ flex: 1 }}
                />
              </FieldsContainer>

              <SearchContainer ref={serachContainerRef}>
                {users.map((user) => (
                  <SearchCard
                    key={user.id}
                    onClick={() => {
                      setSelectedUser(user)
                      setName(user.name)
                      setEmail(user.email)
                      setPassword("*******")
                    }}
                  >
                    <h3>{user.name}</h3>
                    <p>{user.email}</p>
                  </SearchCard>
                ))}
              </SearchContainer>

              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageClick={handlePageClick}
              />

              <ButtonsContainer>
                <RegistersButton type="submit">Salvar</RegistersButton>
                <RegistersButton type="reset" onClick={handleClear}>
                  Limpar
                </RegistersButton>
              </ButtonsContainer>
            </StyledForm>
          </GroupBox>
        </Content>
      </Main>
    </Container>
  )
}

export default Users
